// src/components/NavBar.tsx
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FaHome, FaBriefcase, FaTools, FaProjectDiagram, FaEnvelope, FaGithub, FaLinkedin, FaTimes } from 'react-icons/fa';
import { playClickSound } from '../utils/sounds';
import './Navbar.css';
import netflixLogo from '../images/logo-2.png';
import blueImage from '../images/blue.png';

const Navbar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const profileImage = location.state?.profileImage || blueImage;

  // Get current profile from URL params or route state
  const getCurrentProfile = () => {
    // First, check if we have profile info from route state
    if (location.state?.fromProfile) {
      return location.state.fromProfile;
    }
    
    // Then check URL params for direct profile page visits
    const pathSegments = location.pathname.split('/');
    if (pathSegments[1] === 'profile' && pathSegments[2]) {
      return pathSegments[2];
    }
    
    // Default to recruiter if no profile is found
    return 'recruiter';
  };

  const currentProfile = getCurrentProfile();

  const handleScroll = () => {
    setIsScrolled(window.scrollY > 80);
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleSidebar = () => {
    playClickSound();
    setIsSidebarOpen(!isSidebarOpen);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  const handleNavClick = () => {
    playClickSound();
    closeSidebar();
  };

  const handleNavLinkClick = (route: string) => {
    playClickSound();
    if (route.startsWith('/profile/')) {
      // Direct navigation to profile page
      navigate(route);
    } else {
      // Navigation to other pages - pass current profile state
      navigate(route, { state: { fromProfile: currentProfile } });
    }
  };

  const handleSidebarNavClick = (route: string) => {
    playClickSound();
    closeSidebar();
    if (route.startsWith('/profile/')) {
      // Direct navigation to profile page
      navigate(route);
    } else {
      // Navigation to other pages - pass current profile state
      navigate(route, { state: { fromProfile: currentProfile } });
    }
  };

  const handleLogoClick = () => {
    playClickSound();
    navigate('/');
  };

  const handleProfileClick = () => {
    playClickSound();
    navigate('/browse');
  };

  // LinkedIn and GitHub profile URLs from your content
  const linkedInUrl = "https://www.linkedin.com/in/mantavya-mahajan-42972721b/";
  const githubUrl = "https://github.com/mantavya0807";

  return (
    <>
      <nav className={`navbar ${isScrolled ? 'scrolled' : ''}`}>
        <div className="navbar-left">
          <Link to="/" className="navbar-logo" onClick={handleLogoClick}>
            <img src={netflixLogo} alt="Netflix" />
          </Link>
          <ul className="navbar-links">
            <li><Link to={`/profile/${currentProfile}`} onClick={playClickSound}>Home</Link></li>
            <li><a href={linkedInUrl} target="_blank" rel="noopener noreferrer" onClick={playClickSound}>LinkedIn</a></li>
            <li><a href={githubUrl} target="_blank" rel="noopener noreferrer" onClick={playClickSound}>GitHub</a></li>
            <li><button onClick={() => handleNavLinkClick('/work-experience')} className="nav-link-button">Experience</button></li>
            <li><button onClick={() => handleNavLinkClick('/contact-me')} className="nav-link-button">Contact</button></li>
          </ul>
        </div>
        <div className="navbar-right">
          {/* Hamburger menu for mobile */}
          <div className={`hamburger ${isSidebarOpen ? 'active' : ''}`} onClick={toggleSidebar}>
            <div></div>
            <div></div>
            <div></div>
          </div>
          <img src={profileImage} alt="Profile" className="profile-icon" onClick={handleProfileClick} />
        </div>
      </nav>

      {/* Sidebar Overlay */}
      <div className={`sidebar-overlay ${isSidebarOpen ? 'open' : ''}`} onClick={closeSidebar}></div>

      {/* Sidebar (only visible on mobile) */}
      <div className={`sidebar ${isSidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <img src={netflixLogo} alt="Netflix Logo" />
          </div>
          <button className="close-btn" onClick={closeSidebar}>
            <FaTimes />
          </button>
        </div>
        <ul>
          <li><Link to={`/profile/${currentProfile}`} onClick={handleNavClick}><FaHome /> Home</Link></li>
          <li><a href={linkedInUrl} target="_blank" rel="noopener noreferrer" onClick={handleNavClick}><FaLinkedin /> LinkedIn</a></li>
          <li><a href={githubUrl} target="_blank" rel="noopener noreferrer" onClick={handleNavClick}><FaGithub /> GitHub</a></li>
          <li><button onClick={() => handleSidebarNavClick('/work-experience')} className="nav-link-button"><FaBriefcase /> Experience</button></li>
          <li><button onClick={() => handleSidebarNavClick('/skills')} className="nav-link-button"><FaTools /> Skills</button></li>
          <li><button onClick={() => handleSidebarNavClick('/projects')} className="nav-link-button"><FaProjectDiagram /> Projects</button></li>
          <li><button onClick={() => handleSidebarNavClick('/contact-me')} className="nav-link-button"><FaEnvelope /> Contact</button></li>
        </ul>
      </div>
    </>
  );
};

export default Navbar;